import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CreditCard, Lock, AlertCircle, CheckCircle } from 'lucide-react';

// Security: displayed amount is informative only. Backend calculates exact 50%
// from the quote already accepted by the signed-in customer.
export default function PaymentForm({ jobId, quoteVersion, amount, onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (processing) return;
    if (!stripe || !elements || !jobId || !Number.isSafeInteger(quoteVersion)) {
      setError('Payment is not ready. Please refresh and try again.');
      return;
    }
    setProcessing(true); setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'createDepositPaymentIntent');
      const result = await fn({ jobId, version: quoteVersion });
      const { clientSecret, amountCents, version } = result.data;
      if (version !== quoteVersion || !clientSecret || !Number.isSafeInteger(amountCents)) {
        throw new Error('The quote changed. Refresh and review it again.');
      }
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement) }
      });
      if (stripeError) throw stripeError;
      if (paymentIntent?.status !== 'succeeded') {
        throw new Error('Payment is not complete yet. Please check again before retrying.');
      }
      // Do NOT update depositPaid/status from the browser: webhook will verify it.
      setSubmitted(true);
      onSuccess?.(paymentIntent);
    } catch (err) {
      const message = err?.message || 'Payment could not be completed.';
      setError(message);
      onError?.(err);
    } finally {
      setProcessing(false);
    }
  };

  if (submitted) return (
    <div className="p-5 bg-green-50 border border-green-200 rounded-lg">
      <div className="flex gap-2 items-center text-green-900 font-semibold"><CheckCircle size={20} /> Card payment submitted</div>
      <p className="text-sm text-green-800 mt-2">Stripe is confirming the deposit with Chuko Logic. Your job status will update when payment verification completes.</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-2 font-semibold text-lg"><CreditCard size={20}/> Pay the 50% deposit</div>
      {typeof amount === 'number' && <div className="text-xl font-bold">${amount.toFixed(2)}</div>}
      <p className="text-sm text-gray-600">The payment is calculated from your accepted quote version {quoteVersion}.</p>
      <label className="block text-sm font-medium">Card details
        <div className="mt-2 p-3 border border-gray-300 rounded-lg"><CardElement options={{ style: {base: { fontSize: '16px' }} }} /></div>
      </label>
      {error && <p role="alert" className="flex gap-2 p-3 bg-red-50 text-red-800 rounded-lg"><AlertCircle size={18}/>{error}</p>}
      <button disabled={!stripe || processing} type="submit" className="w-full bg-blue-700 text-white rounded-lg py-3 font-semibold disabled:opacity-50 flex items-center justify-center gap-2"><Lock size={17}/>{processing ? 'Processing…' : 'Pay deposit securely'}</button>
      <p className="text-xs text-gray-500">Powered by Stripe. Card information is entered into Stripe Elements. This checkout does not request permission to save your card for future off-session payments.</p>
    </form>
  );
}
